import { registerBrick, BrickContext } from 'olympe';
import { ReactBrick, useProperty, getLogger } from '@olympeio/core';
import React, { useEffect, useState, ReactElement } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Autocomplete, TextField, Typography, Tooltip } from '@mui/material';
import { OperationalBuyerFilters } from './OperationalBuyerFilters.jsx';
import { GoodReceiverFilters } from './GoodReceiverFilters.jsx';
import { getFilterDrawer } from '../shared/FilterDrawer.jsx';
import { MasterDataAutoComplete } from '../shared/MasterDataAutoComplete.jsx';
import { rolesOptions } from '../shared/_enums.js';

class BlockedFilterDrawer extends ReactBrick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @returns {function(object): ReactElement}
     */
    static getReactComponent($) {
        const MAX_DROPDOWN_CONTENT = 500;
        const getOptions = $.get('get filter options');

        return () => {
            const logger = getLogger('BlockedFilterDrawer');

            // Variables declaration
            const [selectedFilters, setSelectedFilters] = useState({});
            const [role, setRole] = useState(rolesOptions[0]);
            const [companyCodes, setCompanyCodes] = useState([]);
            const [agents, setAgents] = useState([]);
            const [positions, setPositions] = useState([]);
            const [responsibilities, setResponsibilities] = useState([]);
            const [formInvalid, setFormInvalid] = useState(true);
            const [approversOptions, setApproversOptions] = useState([]);

            // listen to properties
            const open = useProperty($, 'open');
            const data = useProperty($, 'filterValues') || {};

            useEffect(() => {
                setFormInvalid(
                    selectedFilters.length === 0 ||
                        (companyCodes.length === 0 && agents.length === 0 && positions.length === 0) ||
                        role === null
                );
                selectedFilters.companyCodes = companyCodes;
                selectedFilters.agents = agents;
                selectedFilters.role = [role];
                selectedFilters.positions = positions;
                selectedFilters.responsibilities = responsibilities;
                setSelectedFilters(selectedFilters);
            }, [companyCodes, role, positions, responsibilities, agents]);

            // Register the subscription only once
            useEffect(() => {
                const myObs = $.observe('selectedFilters').subscribe((newSelectedFilters) => {
                    setCompanyCodes(newSelectedFilters.companyCodes);
                    setAgents(newSelectedFilters.agents);
                    setRole(newSelectedFilters.role?.[0]);
                    setPositions(newSelectedFilters.positions);
                    setResponsibilities(newSelectedFilters.responsibilities);
                    setSelectedFilters(newSelectedFilters);
                });
                return () => {
                    myObs.unsubscribe();
                };
            }, []);

            // In order to avoid a too long rendering adding too much useless elements in the dropdown, we slice the collection
            // Only for those that have data collection greater than 2000 lines
            useEffect(() => {
                if (data.Approvers !== undefined && approversOptions.length === 0) {
                    setApproversOptions(data.Approvers.slice(0, MAX_DROPDOWN_CONTENT));
                }
            }, [data]);

            // Close popup method
            const toggleDrawer = (open) => {
                return (event) => {
                    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
                        return;
                    }

                    $.set('open', open);
                };
            };

            // Forward data to draw & close the popup when applying
            const onApply = (event) => {
                $.set('selectedFilters', JSON.parse(JSON.stringify(selectedFilters)));
                toggleDrawer(false)(event);
            };

            // Display the form
            const getFilterList = () => (
                <Box role="presentation" onClose={toggleDrawer(false)} sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Autocomplete
                                id="buyer-type"
                                options={rolesOptions}
                                getOptionLabel={(option) => option.role}
                                renderInput={(params) => <TextField {...params} label="Role" />}
                                onChange={(e, v) => setRole(v)}
                                isOptionEqualToValue={(option, value) => option.rule === value.rule}
                                value={role || []}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <hr style={{ borderTop: '2px dashed #CCC' }} />
                            <Typography variant="caption">
                                Please select at least one of the following 3 filters
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Autocomplete
                                id="company-code"
                                options={data.CompanyCode || []}
                                getOptionLabel={(option) => option?.CompCode + ' - ' + option?.CompCodeDesc}
                                renderInput={(params) => <TextField {...params} label="Company Code" />}
                                onChange={(e, v) => setCompanyCodes(v)}
                                isOptionEqualToValue={(option, value) => option.CompCode === value.CompCode}
                                value={companyCodes || []}
                                fullWidth
                                multiple
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Tooltip
                                title={positions?.length > 0 ? 'Unselect Position filter to add a User filter' : ''}
                                placement={'top'}
                                arrow
                            >
                                <MasterDataAutoComplete
                                    context={$}
                                    logger={logger}
                                    getOptions={getOptions}
                                    path={'ApproverSet'}
                                    filter={(value) => `IdNo eq 'B1' and SearchTerm eq '${value}'`}
                                    value={agents ?? []}
                                    getOptionLabel={(option) =>
                                        option ? `${option.ApproverId} - ${option.ApproverName}` : ''
                                    }
                                    isOptionEqualToValue={(option, value) => option.ApproverId === value.ApproverId}
                                    onChange={(value) => setAgents(value)}
                                    textFieldLabel="User ID"
                                    multiple={true}
                                    disabled={positions?.length > 0}
                                />
                            </Tooltip>
                        </Grid>

                        <Grid item xs={12}>
                            <Tooltip
                                title={agents?.length > 0 ? 'Unselect User filter to add a Position filter' : ''}
                                placement={'top'}
                                arrow
                            >
                                <MasterDataAutoComplete
                                    context={$}
                                    logger={logger}
                                    getOptions={getOptions}
                                    path={'PositionIdSet'}
                                    filter={(value) => `IdNo eq '01' and SearchTerm eq '${value}'`}
                                    value={positions ?? []}
                                    getOptionLabel={(option) => (option ? `${option.PosId} - ${option.PosIdName}` : '')}
                                    isOptionEqualToValue={(option, value) => option.PosId === value.PosId}
                                    onChange={(value) => setPositions(value)}
                                    textFieldLabel="Position"
                                    multiple={true}
                                    disabled={agents?.length > 0}
                                />
                            </Tooltip>
                        </Grid>

                        <Grid item xs={12}>
                            <hr style={{ borderTop: '2px dashed #CCC' }} />
                        </Grid>

                        <Grid item xs={12}>
                            <MasterDataAutoComplete
                                context={$}
                                logger={logger}
                                getOptions={getOptions}
                                path={'RespSearchSet'}
                                filter={(value) =>
                                    `IdNo eq '01' and Rule eq '${role.rule}' and SearchTerm eq '${value}'`
                                }
                                value={responsibilities ?? []}
                                getOptionLabel={(option) => (option ? `${option.RespNo} - ${option.RespShort}` : '')}
                                isOptionEqualToValue={(option, value) => option.RespNo === value.RespNo}
                                onChange={(value) => setResponsibilities(value)}
                                textFieldLabel="Responsibility search"
                                multiple={true}
                            />
                        </Grid>
                    </Grid>

                    {(role?.rule === '91200020' || role?.rule === '91200028') && (
                        <OperationalBuyerFilters
                            data={data}
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                        />
                    )}
                    {role?.rule === '91200026' && (
                        <GoodReceiverFilters
                            data={data}
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                            context={$}
                            getOptions={getOptions}
                        />
                    )}
                </Box>
            );

            return getFilterDrawer(
                open,
                formInvalid,
                'You must select a company code to apply filters',
                toggleDrawer,
                getFilterList,
                onApply,
                data
            );
        };
    }
}

registerBrick('018adba4dfc3ba8fe93c', BlockedFilterDrawer);
